import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Card, useBoardArchive, useDeleteCard, useSetCardArchived } from '@/entities/board';
import { useViewer } from '@/entities/viewer';
import { ErrorState, Button, ConfirmDialog, Dialog, LoadingState } from '@/shared/ui';
import { formatDateTime } from '@/shared/lib/datetime';
import { FormError } from '@/shared/forms';

import styles from './Recovery.module.css';

/**
 * Archived cards of the board. They are not on the Kanban board; restoring returns
 * a card to its old place and keeps its completed/pinned state. The search runs on the server over
 * the whole archive the viewer may see. Deleting an archived card sends it to the board's TRASH
 * (kept 7 days, restorable there) - the same deletion as everywhere else.
 * The archive itself is not the trash: nothing in it is scheduled for deletion.
 */
export function ArchiveDialog({
  boardId,
  canDelete = false,
  onClose,
}: {
  boardId: string;
  /** The viewer may delete tasks (the server decides; this only offers the control). */
  canDelete?: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const timeZone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const [q, setQ] = useState('');
  const archive = useBoardArchive(boardId, true, q);
  const restore = useSetCardArchived(boardId);
  const remove = useDeleteCard(boardId);
  const [deleting, setDeleting] = useState<Card | null>(null);
  const searching = q.trim() !== '';

  return (
    <Dialog title={t('archive.title')} onClose={onClose} size="lg">
      <p className={styles.hint}>{t('archive.hint')}</p>
      <input
        type="search"
        className={styles.search}
        value={q}
        placeholder={t('archive.search')}
        aria-label={t('archive.search')}
        maxLength={200}
        onChange={(event) => {
          setQ(event.target.value);
        }}
      />
      <FormError error={restore.error ?? remove.error} />
      {archive.isPending && <LoadingState />}
      {archive.isError && (
        <ErrorState error={archive.error} onRetry={() => void archive.refetch()} />
      )}
      {archive.data?.length === 0 && (
        <p className={styles.empty} role="status">
          {searching ? t('archive.noMatches') : t('archive.empty')}
        </p>
      )}
      {archive.data && archive.data.length > 0 && (
        <ul className={styles.list} aria-label={t('archive.title')}>
          {archive.data.map((card) => (
            <li key={card.id} className={styles.item}>
              <div className={styles.itemText}>
                <span className={styles.itemTitle}>{card.title}</span>
                {card.completed_at && (
                  <span className={styles.itemMeta}>
                    {t('archive.completedAt', {
                      when: formatDateTime(card.completed_at, i18n.language, timeZone),
                    })}
                  </span>
                )}
              </div>
              <div className={styles.itemActions}>
                <Button
                  size="sm"
                  disabled={restore.isPending}
                  aria-label={t('archive.restoreOne', { title: card.title })}
                  onClick={() => {
                    restore.mutate({ cardId: card.id, archived: false });
                  }}
                >
                  {t('archive.restore')}
                </Button>
                {canDelete && (
                  <Button
                    size="sm"
                    variant="danger"
                    aria-label={t('archive.deleteOne', { title: card.title })}
                    onClick={() => {
                      setDeleting(card);
                    }}
                  >
                    {t('archive.delete')}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {deleting && (
        <ConfirmDialog
          title={t('archive.deleteTitle')}
          message={t('archive.deleteMessage', { title: deleting.title })}
          confirmLabel={t('archive.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setDeleting(null);
          }}
          onConfirm={() => {
            remove.mutate(deleting.id, {
              onSettled: () => {
                setDeleting(null);
              },
            });
          }}
        />
      )}
    </Dialog>
  );
}
